import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiCall } from '../lib/api';
import type { ListCouponsOutput } from '@meso/types';

export function Coupons() {
  const qc = useQueryClient();
  const [infoId, setInfoId] = useState<string | null>(null);

  const list = useQuery<ListCouponsOutput>({
    queryKey: ['coupons'],
    queryFn: () => apiCall<ListCouponsOutput>('get', '/coupons'),
  });

  const claim = useMutation({
    mutationFn: (coupon_id: string) => apiCall('post', '/coupons/claim', { coupon_id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['coupons'] }),
  });

  if (list.isLoading) return <div className="p-6 text-center text-gray-500">กำลังโหลด...</div>;

  const claimedIds = new Set(list.data?.claimed.map((c) => c.master_id) ?? []);
  const available = list.data?.available ?? [];

  return (
    <div className="max-w-md mx-auto px-4 py-6">
      <h1 className="text-xl font-bold mb-4">รับคูปอง</h1>

      {available.length === 0 && (
        <div className="card text-center py-12 text-gray-500">
          ยังไม่มีคูปองในขณะนี้
        </div>
      )}

      <div className="space-y-3">
        {available.map((c) => {
          const claimed     = claimedIds.has(c.id);
          const now         = Date.now();
          // คูปองหมด — รับครบจำนวนที่กำหนดแล้ว
          const isExhausted = c.max_claims != null && Number(c.claimed_count ?? 0) >= Number(c.max_claims);
          // คูปองหมดอายุ
          const isExpired   = c.expires_at != null && new Date(c.expires_at).getTime() < now;
          const disabled    = isExhausted || isExpired;

          return (
            <div key={c.id} className={`card flex items-center gap-3 p-0 overflow-hidden
                                        ${disabled && !claimed ? 'opacity-60 grayscale' : ''}`}>
              <div className={`px-4 py-6 flex flex-col items-center justify-center min-w-[96px]
                               ${disabled && !claimed ? 'bg-gray-400 text-white' : 'bg-black text-white'}`}>
                <div className="text-xl font-bold leading-none">{c.value}</div>
                <div className="text-xs opacity-80 mt-1">{c.unit === 'baht' ? 'บาท' : '%'}</div>
              </div>
              <div className="flex-1 p-3">
                <div className="font-bold text-sm">{c.title}</div>
                {c.description && <div className="text-xs text-gray-500 mt-1">{c.description}</div>}

                {/* แสดงจำนวนคงเหลือ ถ้ามีการจำกัด */}
                {c.max_claims != null && (
                  <div className={`text-[11px] mt-1 font-medium
                                   ${isExhausted ? 'text-red-600' : 'text-gray-500'}`}>
                    🎫 {isExhausted
                      ? <>หมดแล้ว ({Number(c.claimed_count ?? 0)}/{c.max_claims})</>
                      : <>เหลือ {Number(c.max_claims) - Number(c.claimed_count ?? 0)} จาก {c.max_claims} ใบ</>}
                  </div>
                )}

                {c.min_purchase != null && Number(c.min_purchase) > 0 && (
                  <div className="text-[11px] text-rose-600 mt-1 font-medium">
                    💵 ใช้ได้เมื่อยอดสั่งซื้อขั้นต่ำ ฿{Number(c.min_purchase).toLocaleString('th-TH')}
                  </div>
                )}
                {c.expires_at && (
                  <div className={`text-[11px] mt-1 ${isExpired ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>
                    {isExpired ? '⏱ หมดอายุแล้ว' : 'หมดอายุ'} {new Date(c.expires_at).toLocaleDateString('th-TH')}
                  </div>
                )}

                <div className="mt-2 flex items-center gap-2">
                  {claimed ? (
                    <span className="text-xs px-3 py-1 bg-gray-200 rounded-full text-gray-600">รับแล้ว</span>
                  ) : isExhausted ? (
                    <span className="text-xs px-3 py-1 bg-red-100 text-red-700 rounded-full font-semibold">
                      🎫 หมดแล้ว
                    </span>
                  ) : isExpired ? (
                    <span className="text-xs px-3 py-1 bg-gray-200 text-gray-600 rounded-full font-semibold">
                      ⏱ หมดอายุ
                    </span>
                  ) : (
                    <button
                      onClick={() => claim.mutate(c.id)}
                      disabled={claim.isPending}
                      className="btn btn-primary text-xs px-4 py-1.5"
                    >
                      รับคูปอง
                    </button>
                  )}
                  <button
                    onClick={() => setInfoId(c.id)}
                    className="text-xs text-blue-600 underline ml-auto"
                  >
                    ดูรายละเอียด
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {infoId && <CouponInfoModal couponId={infoId} onClose={() => setInfoId(null)} />}
    </div>
  );
}

// ─── Detail modal (fetches Shopify info via /coupons/:id/info) ──────────
interface CouponInfo {
  master: {
    id: string; title: string; description: string | null;
    value: number; unit: 'baht' | 'percent';
    min_purchase: number | null; expires_at: string | null;
    max_claims: number | null; claimed_count: number;
    discount_code: string | null; active: boolean;
  };
  shopify: null | {
    id: string; title: string; status: string; code: string;
    startsAt: string; endsAt: string | null;
    appliesOncePerCustomer: boolean;
    usageLimit: number | null; asyncUsageCount: number;
    combinesWith: { orderDiscounts: boolean; productDiscounts: boolean; shippingDiscounts: boolean };
    valueLabel: string;
    minPurchaseAmount: number | null; minQuantity: number | null;
    customerEligibility: string;
    appliesTo: string;
    appliesToCollections: Array<{ title: string; handle: string }>;
    appliesToProducts: Array<{ title: string }>;
  };
}

function CouponInfoModal({ couponId, onClose }: { couponId: string; onClose: () => void }) {
  const q = useQuery<CouponInfo>({
    queryKey: ['coupon-info', couponId],
    queryFn: () => apiCall<CouponInfo>('get', `/coupons/${couponId}/info`),
  });

  return (
    <div className="fixed inset-0 z-50 flex flex-col">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="bg-white rounded-t-2xl px-4 py-5 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center mb-3">
          <h2 className="text-lg font-bold flex-1">รายละเอียดคูปอง</h2>
          <button onClick={onClose} className="text-gray-400 text-2xl leading-none">×</button>
        </div>

        {q.isLoading && (
          <div className="text-center text-gray-500 py-10">กำลังโหลด...</div>
        )}

        {q.data && (
          <div className="space-y-4">
            {/* CRM master info */}
            <div className="card bg-rose-50 border border-rose-200">
              <div className="flex items-center gap-3">
                <div className="bg-rose-600 text-white px-3 py-3 rounded-lg flex flex-col items-center min-w-[72px]">
                  <div className="text-2xl font-bold leading-none">{q.data.master.value}</div>
                  <div className="text-[10px] mt-1">{q.data.master.unit === 'baht' ? 'บาท' : '%'}</div>
                </div>
                <div className="flex-1">
                  <div className="font-bold">{q.data.master.title}</div>
                  {q.data.master.description && (
                    <div className="text-xs text-gray-600 mt-0.5">{q.data.master.description}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Shopify discount info */}
            {q.data.shopify ? (
              <div className="card border border-gray-200 space-y-2.5">
                <div className="flex items-center gap-2 -mx-4 -mt-4 mb-2 px-4 py-2.5 rounded-t-2xl bg-emerald-50">
                  <span className="text-sm">🛒</span>
                  <span className="font-semibold text-sm text-emerald-700 flex-1">เงื่อนไขจาก Shopify</span>
                  <StatusBadge status={q.data.shopify.status} />
                </div>

                <InfoRow label="โค้ด" value={
                  <code className="font-mono text-sm bg-gray-100 px-2 py-0.5 rounded">{q.data.shopify.code}</code>
                } />
                <InfoRow label="มูลค่า" value={
                  <span className="font-bold text-rose-600">{q.data.shopify.valueLabel}</span>
                } />

                {q.data.shopify.minPurchaseAmount != null && (
                  <InfoRow label="ขั้นต่ำในการซื้อ" value={
                    <span className="font-semibold">฿{q.data.shopify.minPurchaseAmount.toLocaleString('th-TH')}</span>
                  } />
                )}
                {q.data.shopify.minQuantity != null && (
                  <InfoRow label="จำนวนชิ้นขั้นต่ำ" value={`${q.data.shopify.minQuantity} ชิ้น`} />
                )}

                <InfoRow label="สิทธิ์การใช้งาน" value={
                  <span className={q.data.shopify.customerEligibility.startsWith('เฉพาะ')
                    ? 'text-amber-700 font-medium' : ''}>
                    {q.data.shopify.customerEligibility}
                  </span>
                } />

                <InfoRow label="ใช้ได้กับ" value={
                  <span>
                    {q.data.shopify.appliesTo}
                    {q.data.shopify.appliesToCollections.length > 0 && (
                      <div className="mt-1 text-[11px] text-gray-500">
                        {q.data.shopify.appliesToCollections.map((c) => c.title).join(' · ')}
                      </div>
                    )}
                    {q.data.shopify.appliesToProducts.length > 0 && (
                      <div className="mt-1 text-[11px] text-gray-500">
                        {q.data.shopify.appliesToProducts.map((p) => p.title).join(' · ')}
                      </div>
                    )}
                  </span>
                } />

                {q.data.shopify.appliesOncePerCustomer && (
                  <InfoRow label="ข้อจำกัด" value="ใช้ได้ครั้งเดียวต่อลูกค้าหนึ่งราย" />
                )}
                {q.data.shopify.usageLimit != null && (
                  <InfoRow label="จำนวนสูงสุด" value={
                    `${q.data.shopify.asyncUsageCount} / ${q.data.shopify.usageLimit} ครั้ง`
                  } />
                )}

                <InfoRow label="วันที่เริ่ม" value={
                  new Date(q.data.shopify.startsAt).toLocaleString('th-TH')
                } />
                {q.data.shopify.endsAt && (
                  <InfoRow label="วันหมดอายุ" value={
                    new Date(q.data.shopify.endsAt).toLocaleString('th-TH')
                  } />
                )}
              </div>
            ) : q.data.master.discount_code ? (
              <div className="card bg-amber-50 border border-amber-200 text-sm text-amber-800">
                ⚠️ ยังเชื่อมข้อมูลกับ Shopify ไม่ได้ — code <code>{q.data.master.discount_code}</code> อาจยังไม่ได้สร้างใน Shopify
              </div>
            ) : (
              <div className="card bg-gray-50 border border-gray-200 text-sm text-gray-600">
                คูปองนี้ยังไม่ได้ผูกกับ Shopify discount code
              </div>
            )}
          </div>
        )}

        <button onClick={onClose} className="btn btn-block mt-4 border border-gray-300">
          ปิด
        </button>
      </div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 py-1.5 border-b border-gray-100 last:border-0">
      <div className="text-xs text-gray-500 min-w-[100px] mt-0.5">{label}</div>
      <div className="flex-1 text-sm text-gray-900">{value}</div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    ACTIVE:    { label: '✓ Active',    cls: 'bg-emerald-100 text-emerald-700' },
    EXPIRED:   { label: '⏱ Expired',   cls: 'bg-gray-200 text-gray-600' },
    SCHEDULED: { label: '⏳ Scheduled', cls: 'bg-blue-100 text-blue-700' },
  };
  const m = map[status] ?? { label: status, cls: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${m.cls}`}>
      {m.label}
    </span>
  );
}
